import React, { useState, useEffect } from "react";
import { StyleSheet, View, TouchableOpacity, Platform } from "react-native";
import { Image } from "expo-image";
import {
  MemoryModel,
  ShareModel,
  Contact,
  ContactModel,
  GroupModel,
  IMediaMetadata,
  MediaModel,
  IDate,
  formatLocation,
  formatDisplayDate,
  formatCardTimestamp,
  ICustomCardEntityConfig,
} from "common247";
import { ThemedText } from "../ThemedText";
import { ShareSelector } from "../sharing/ShareSelector";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ButtonPaddings } from "@/constants/themes/components";
import { Ionicons } from "@expo/vector-icons";
import { hasExtractedMetadata } from "@/services/media-metadata";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import type { ThemedListViewMode } from "../ThemedList";
import MediaGallery from "../media/MediaGallery";
import TagListStripDefault, { TagListStrip as TagListStripNamed } from "@/components/tags/TagListStrip";
const TagListStrip = TagListStripNamed || TagListStripDefault;
import { ThemedRating } from "../ThemedRating";
import { ThemedChip } from "../ThemedChip";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedMediumCard } from "../cards/ThemedMediumCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import {
  resolveCardLinkedContacts,
  resolveCardLinkedGroups,
  resolveCardLinkedMedia,
  resolveCardLinkedPlaces,
  resolveCardLinkedAnchors,
} from "../cards/card-helpers";
import { useFirebase } from "../firebase/useFirebase";
import { editMemory } from "@/services/firebase/memory-functions";

/**
 * Props for the MemoryCard component.
 */
export interface IMemoryCardProps {
  memory: MemoryModel;
  onPress?: () => void;
  onLongPress?: () => void;
  onContextMenu?: (e?: any) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onShare?: () => void;
  onEditPress?: () => void;
  onDelete?: () => void;
  showOwner?: boolean;
  /** Whether the card is rendered in an administrative management screen */
  isAdminView?: boolean;
  /** Whether the user is allowed to add or remove tags (explicit override) */
  canEditTags?: boolean;
  /** Current authenticated user ID for tag removal authorization */
  currentUserId?: string;
  /** Whether to render in compact density mode */
  compact?: boolean;
  /** View mode from ThemedList (details, small, medium, large) */
  viewMode?: ThemedListViewMode;
  /** Whether this card is currently hovered in the parent list */
  isHovered?: boolean;

  /** Existing shares associated with this memory */
  shares?: ShareModel[];

  /** Available contacts to share with */
  availableContacts?: (Contact | ContactModel)[];

  /** Available groups to share with */
  availableGroups?: GroupModel[];

  /** Available places to resolve linked place metadata */
  availablePlaces?: any[];

  /** Available anchors to resolve linked anchor metadata */
  availableAnchors?: any[];

  /** Loading state for shares query */
  isLoadingShares?: boolean;

  /** Callback when sharing with a target */
  onShareTarget?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;

  /** Callback when removing a share */
  onUnshare?: (shareId: string) => void;

  /** Whether the inline sharing manager is expanded */
  isSharingExpanded?: boolean;

  /** Toggle callback for inline sharing manager */
  onToggleSharing?: () => void;

  /**
   * Optional aggregated metadata extracted from media linked to this memory.
   * Populated by the caller when media objects are available.
   */
  extractedMetadata?: IMediaMetadata;

  /** Callback to send/export this memory (e.g. to PDF) */
  onSendTo?: (memory: MemoryModel) => void;

  /**
   * Optional callback when a media item inside the memory is pressed.
   */
  onMediaPress?: (media: MediaModel) => void;

  /**
   * Optional callback fired when the memory date is updated via manual typing or DatePicker.
   */
  onDateChange?: (newDate: string, payload?: IDate) => void;

  /**
   * Whether inline date editing is enabled on this card.
   * If omitted, defaults to whether the user can edit the memory (isOwner || userRole === 'editor').
   */
  isDateEditable?: boolean;

  /** Optional custom card configuration */
  config?: ICustomCardEntityConfig;

  /**
   * Optional callback when the favourite rating is changed.
   */
  onRateChange?: (newRating: number) => void;
}

/**
 * Inline display of offline-extracted metadata within a MemoryCard.
 */
import { MemoryMetadataSection } from "./MemoryMetadataSection";
export { MemoryMetadataSection };

/**
 * MemoryCard component displaying memory details with inline SharingControl integration.
 */
export function MemoryCard({
  memory,
  onPress,
  onLongPress,
  onContextMenu,
  selectionMode = false,
  isSelected = false,
  onShare,
  onEditPress,
  onDelete,
  showOwner = true,
  isAdminView = false,
  canEditTags,
  currentUserId,
  compact = false,
  viewMode,
  isHovered,
  shares,
  availableContacts,
  availableGroups,
  availablePlaces,
  availableAnchors,
  isLoadingShares,
  onShareTarget,
  onUnshare,
  isSharingExpanded,
  onToggleSharing,
  extractedMetadata,
  onSendTo,
  onMediaPress,
  onDateChange,
  isDateEditable,
  onRateChange,
  config,
}: IMemoryCardProps) {
  const services = useFirebase();
  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const tintColor = useThemeColor({}, "tint");
  const dangerColor = useThemeColor({}, "danger");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const activeBorderColor = isSelected ? tintColor : isHovered ? hoverBorderColor : borderColor;
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isSharingExpanded !== undefined ? isSharingExpanded : localExpanded;

  const resolvedMedia = resolveCardLinkedMedia(memory);
  const effectiveMedia = resolvedMedia.length > 0 ? resolvedMedia : (memory.mediaItems || []);
  const firstMediaUrl = (
    memory.photoIconUrl ||
    memory.photoUrl ||
    (effectiveMedia.length > 0 ? (effectiveMedia[0]?.downloadUrl || (effectiveMedia[0] as any)?.url) : undefined)
  )?.trim() || undefined;

  const [lastKnownShares, setLastKnownShares] = useState<ShareModel[] | null>(null);

  useEffect(() => {
    if (shares !== undefined) {
      setLastKnownShares(shares);
    }
  }, [shares]);

  const isOwner = isAdminView ? false : memory.userRole === "owner";
  const isViewer = isAdminView ? true : memory.userRole === "viewer";
  const canEdit = !isAdminView && (isOwner || memory.userRole === "editor");
  const effectiveCanEditTags = canEditTags !== undefined ? canEditTags : (!isAdminView && canEdit);
  const effectiveIsOwnerForTags = (canEditTags === false || isAdminView) ? false : isOwner;
  const roleName = isOwner ? undefined : isViewer ? "Shared Memory (View Only)" : "Shared Memory";
  const cardDate = formatDisplayDate(memory.date);

  const [rating, setRating] = useState<number>(memory.favouriteRating ?? 3);

  useEffect(() => {
    if (memory.favouriteRating !== undefined) {
      setRating(memory.favouriteRating);
    }
  }, [memory.favouriteRating]);

  const handleRatingChange = async (newRating: number) => {
    setRating(newRating);
    if (onRateChange) {
      onRateChange(newRating);
    }
    if (services && memory.id && canEdit) {
      try {
        await editMemory(services, {
          memoryId: memory.id,
          favouriteRating: newRating,
        });
      } catch (err) {
        console.warn("Failed to update memory favourite rating:", err);
      }
    }
  };

  const handleSharePress = () => {
    if (onToggleSharing) {
      onToggleSharing();
    } else if (onShare) {
      onShare();
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  const effectiveShares: ShareModel[] =
    shares ??
    lastKnownShares ??
    (memory.sharedWith?.map((s) => ({
      id: s.id,
      sourceId: memory.id || "",
      sourceType: "memory" as const,
      targetId: s.id,
      targetType: s.type,
      role: s.role,
      targetDisplayName: s.displayName,
      targetUserId: s.targetUserId,
      sharedBy: memory.ownerId || "",
      sharedAt: memory.createdAt,
    })) as ShareModel[]) ??
    [];

  const effectiveMetadata = extractedMetadata || (memory as any).mediaMetadata || (memory as any).metadata;

  const resolvedContacts = resolveCardLinkedContacts(memory, availableContacts);
  const resolvedGroups = resolveCardLinkedGroups(memory, availableGroups);
  const resolvedPlaces = resolveCardLinkedPlaces(memory, availablePlaces);
  const resolvedAnchors = resolveCardLinkedAnchors(memory, availableAnchors);

  const canShowShareButton = memory.canShare && (onShare || onShareTarget || onToggleSharing || shares !== undefined);

  const kebabActions: IKebabActionItem[] = [];
  if (onSendTo) {
    kebabActions.push({
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: () => onSendTo(memory),
    });
  }
  if (canShowShareButton) {
    kebabActions.push({
      id: "share",
      label: isExpanded ? "Close Sharing" : "Manage Sharing",
      icon: "share-social-outline",
      onPress: handleSharePress,
    });
  }
  if (canEdit && onEditPress) {
    kebabActions.push({
      id: "edit",
      label: "Edit Memory",
      icon: "pencil-outline",
      onPress: onEditPress,
    });
  }
  if (isOwner && onDelete) {
    kebabActions.push({
      id: "delete",
      label: "Delete Memory",
      icon: "trash-outline",
      destructive: true,
      onPress: onDelete,
    });
  }

  if (viewMode === "chip") {
    return (
      <View style={{ margin: 2 }}>
        <ThemedChip
          entityType="memory"
          id={memory.id}
          label={memory.title}
          isOwner={isOwner}
          isFiveStar={rating === 5}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      </View>
    );
  }

  if (viewMode === "medium") {
    return (
      <ThemedMediumCard
        entityType="memory"
        id={memory.id}
        title={memory.title}
        formattedDate={cardDate}
        description={memory.description}
        ownerDisplayName={memory.ownerDisplayName}
        isOwner={isOwner}
        isFiveStar={rating === 5}
        kebabActions={kebabActions}
        mediaList={effectiveMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        placesList={resolvedPlaces}
        anchorsList={resolvedAnchors}
        tagsList={memory.tags}
        sharesList={effectiveShares}
        createdAt={memory.createdAt}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={onDelete}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "full") {
    return (
      <ThemedFullCard
        entityType="memory"
        id={memory.id}
        title={memory.title}
        formattedDate={cardDate}
        description={memory.description}
        ownerDisplayName={memory.ownerDisplayName}
        isOwner={isOwner}
        isFiveStar={rating === 5}
        kebabActions={kebabActions}
        mediaList={effectiveMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        placesList={resolvedPlaces}
        anchorsList={resolvedAnchors}
        tagsList={memory.tags}
        sharesList={effectiveShares}
        createdAt={memory.createdAt}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={onDelete}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "custom") {
    return (
      <ThemedCustomCard
        entityType="memory"
        id={memory.id}
        item={memory}
        title={memory.title}
        formattedDate={cardDate}
        description={memory.description}
        ownerDisplayName={memory.ownerDisplayName}
        isOwner={isOwner}
        isFiveStar={rating === 5}
        kebabActions={kebabActions}
        mediaList={effectiveMedia}
        contactsList={Object.values(memory.contactItems || (memory as any).contactsList || memory.contacts || {})}
        groupsList={Object.values(memory.groupItems || (memory as any).groupsList || memory.groups || {})}
        placesList={resolvedPlaces}
        tagsList={memory.tags}
        sharesList={effectiveShares}
        config={config}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={onDelete}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "details") {
    return (
      <View
        style={[
          styles.detailsRowContainer,
          { backgroundColor: cardBgColor, borderColor: activeBorderColor },
        ]}
      >
        <TouchableOpacity
          style={styles.detailsRowMain}
          onPress={onPress}
          onLongPress={onLongPress}
          delayLongPress={350}
          activeOpacity={0.7}
          {...(Platform.OS === "web" && onContextMenu ? {
            onContextMenu: (e: any) => {
              e.preventDefault();
              onContextMenu(e);
            },
          } : {})}
        >
          {selectionMode || isSelected ? (
            <View style={[styles.detailsCheckbox, isSelected && { backgroundColor: tintColor, borderColor: tintColor }]}>
              {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
            </View>
          ) : null}
          {Boolean(memory.photoIconUrl || memory.photoUrl || (effectiveMedia.length > 0 && effectiveMedia[0]?.downloadUrl)) ? (
            <View style={styles.detailsThumb}>
              {(() => {
                const thumbUri = (memory.photoIconUrl || memory.photoUrl || effectiveMedia[0]?.downloadUrl || "").trim();
                const safeUri = thumbUri.includes(" ") ? encodeURI(thumbUri) : thumbUri;
                return (
                  <Image
                    key={safeUri}
                    recyclingKey={safeUri}
                    source={{ uri: safeUri }}
                    style={styles.detailsImage}
                    contentFit="cover"
                    transition={Platform.OS === "android" ? 0 : 150}
                    cachePolicy="memory-disk"
                  />
                );
              })()}
            </View>
          ) : (
            <View style={[styles.detailsThumb, styles.detailsPlaceholder, { backgroundColor: badgeBg }]}>
              <Ionicons name="book-outline" size={16} color={tintColor} />
            </View>
          )}

          <View style={styles.detailsInfo}>
            <View style={styles.detailsNameRow}>
              {isOwner ? (
                <ThemedPlatformSymbol type="owner" size="small" />
              ) : (
                <ThemedPlatformSymbol type="shared" size="small" />
              )}
              {rating === 5 && (
                <ThemedPlatformSymbol type="fivestar" size="small" />
              )}
              <ThemedText type="defaultSemiBold" style={styles.detailsFileName} numberOfLines={1}>
                {memory.title}
              </ThemedText>
              {!isOwner && roleName ? (
                <View style={[styles.inlineRoleBadge, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.gridRoleBadgeText}>{roleName}</ThemedText>
                </View>
              ) : null}
            </View>

            <View style={styles.detailsSubRow}>
              {cardDate ? (
                <ThemedText style={styles.cardDate} numberOfLines={1}>
                  📅 {cardDate}
                </ThemedText>
              ) : null}
              {formatLocation(memory.location) ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • 📍 {formatLocation(memory.location)}
                </ThemedText>
              ) : null}
              {effectiveMedia.length > 0 ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • {effectiveMedia.length} {effectiveMedia.length === 1 ? "media" : "media items"}
                </ThemedText>
              ) : null}
              {showOwner && memory.ownerDisplayName ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • by {memory.ownerDisplayName}
                </ThemedText>
              ) : null}
              <View style={{ marginLeft: 6 }}>
                <ThemedRating
                  value={rating}
                  onChange={handleRatingChange}
                  size={12}
                  showLabel={false}
                  readOnly={!canEdit}
                />
              </View>
            </View>
            <TagListStrip
              entityId={memory.id || ""}
              entityType="memory"
              tags={memory.tags}
              canEdit={effectiveCanEditTags}
              isOwner={effectiveIsOwnerForTags}
              currentUserId={currentUserId}
              compact={true}
            />
            {effectiveShares.length > 0 && (
              <TouchableOpacity
                onPress={handleSharePress}
                style={{ marginTop: 2, alignSelf: "flex-start" }}
                activeOpacity={0.7}
                testID={`memory-share-pill-details-${memory.id || "card"}`}
              >
                <ThemedText style={[styles.detailsMetaText, { color: tintColor, fontWeight: "600" }]} numberOfLines={1}>
                  👥 Shared ({effectiveShares.length})
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>

          {kebabActions.length > 0 && (
            <View style={styles.detailsTopRightAction}>
              <ThemedKebabMenu
                actions={kebabActions}
                compact={true}
                accessibilityLabel={`Options for ${memory.title}`}
              />
            </View>
          )}
        </TouchableOpacity>

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={memory.title}
          itemType="memory"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={memory.canShare}
          canUnshare={memory.canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`memory-share-picker-details-${memory.id || "card"}`}
        />
      </View>
    );
  }

  if (viewMode === "compact") {
    return (
      <View style={{ position: "relative", width: "100%" }}>
        <ThemedCompactCard
          entityType="memory"
          id={memory.id}
          title={memory.title || "Memory"}
          date={cardDate}
          thumbnailUrl={firstMediaUrl}
          isOwner={isOwner}
          isFiveStar={rating === 5}
          selectionMode={selectionMode}
          isSelected={isSelected}
          onSelect={onPress}
          onPress={onPress}
          onLongPress={onLongPress}
          onContextMenu={onContextMenu}
          kebabActions={kebabActions}
          testID={`memory-compact-card-${memory.id || "card"}`}
        />

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={memory.title}
          itemType="memory"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={memory.canShare}
          canUnshare={memory.canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`memory-share-picker-compact-${memory.id || "card"}`}
        />
      </View>
    );
  }

  const isSmall = viewMode === "small" || compact;
  const isLarge = viewMode === "large";

  return (
    <TouchableOpacity
      style={[
        styles.card,
        isSmall && styles.cardCompact,
        { backgroundColor: cardBgColor, borderColor: activeBorderColor },
      ]}
      onPress={onPress}
      onLongPress={onLongPress}
      delayLongPress={350}
      activeOpacity={0.7}
      {...(Platform.OS === "web" && onContextMenu ? {
        onContextMenu: (e: any) => {
          e.preventDefault();
          onContextMenu(e);
        },
      } : {})}
    >
      <View style={styles.cardHeader}>
        {selectionMode && (
          <TouchableOpacity
            style={[
              styles.detailsCheckbox,
              isSelected && { backgroundColor: tintColor, borderColor: tintColor },
            ]}
            onPress={(e) => {
              e.stopPropagation?.();
              onPress?.();
            }}
          >
            {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
          </TouchableOpacity>
        )}
        <View style={styles.cardHeaderAvatarContainer}>
          {firstMediaUrl ? (
            <Image
              source={{ uri: firstMediaUrl }}
              style={styles.cardHeaderAvatar}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: badgeBg }]}>
              <Ionicons name="book-outline" size={18} color={tintColor} />
            </View>
          )}
        </View>

        <View style={styles.cardHeaderLeft}>
          <ThemedText
            type="defaultSemiBold"
            style={[styles.cardTitle, compact && styles.cardTitleCompact]}
            numberOfLines={1}
          >
            {memory.title}
          </ThemedText>
          <View style={styles.roleBadgeRow}>
            {cardDate ? (
              <ThemedText style={styles.cardLocation} numberOfLines={1}>
                📅 {cardDate}
              </ThemedText>
            ) : null}
            {!isOwner && roleName ? (
              <View style={[styles.roleBadge, { backgroundColor: badgeBg }]}>
                <ThemedText style={styles.roleBadgeText}>{roleName}</ThemedText>
              </View>
            ) : null}
            {!isOwner && showOwner ? (
              <ThemedText style={[styles.ownerText, { color: tintColor }]}>
                • by {memory.ownerDisplayName || memory.ownerId}
              </ThemedText>
            ) : null}
          </View>
        </View>

        <View style={styles.cardTopRightGroup}>
          <View style={styles.symbolsGroup}>
            {isOwner ? (
              <ThemedPlatformSymbol type="owner" size="small" />
            ) : (
              <ThemedPlatformSymbol type="shared" size="small" />
            )}
            {rating === 5 && (
              <ThemedPlatformSymbol type="fivestar" size="small" />
            )}
          </View>
          {kebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={kebabActions}
              compact={compact}
              accessibilityLabel={`Options for ${memory.title}`}
            />
          )}
        </View>
      </View>

      {memory.description ? (
        <ThemedText style={styles.cardDesc} numberOfLines={2}>
          {memory.description}
        </ThemedText>
      ) : (
        <ThemedText style={[styles.cardDesc, styles.noDesc]}>No description.</ThemedText>
      )}

      {formatLocation(memory.location) ? <ThemedText style={styles.cardLocation}>📍 {formatLocation(memory.location)}</ThemedText> : null}

      {cardDate ? (
        <ThemedText style={styles.cardLocation}>📅 {cardDate}</ThemedText>
      ) : null}

      <View style={styles.cardRatingRow}>
        <ThemedRating
          value={rating}
          onChange={handleRatingChange}
          size={16}
          showLabel={true}
          label="Favourite:"
          readOnly={!canEdit}
        />
      </View>

      {/* Media section: reusable MediaGallery + extracted metadata */}
      {(effectiveMedia.length > 0 || (effectiveMetadata && hasExtractedMetadata(effectiveMetadata))) ? (
        <View style={styles.mediaPart}>
          {effectiveMedia.length > 0 ? (
            <MediaGallery
              layout="horizontal"
              embedded={true}
              viewMode={isLarge ? "small" : "compact"}
              data={effectiveMedia}
              ownerId={memory.ownerId}
              createdAt={memory.createdAt}
              compact={compact}
              onMediaPress={onMediaPress}
            />
          ) : null}
          {effectiveMetadata && (hasExtractedMetadata(effectiveMetadata) || (memory as any).mediaTarget) ? (
            <MemoryMetadataSection metadata={effectiveMetadata} mediaTarget={(memory as any).mediaTarget} badgeBg={badgeBg} tintColor={tintColor} />
          ) : null}
        </View>
      ) : null}

      {memory.sharedTo && memory.sharedTo.length > 0 ? (
        <View style={styles.sharesContainer}>
          <ThemedText style={styles.sharesLabel}>Used In: </ThemedText>
          <View style={styles.sharesList}>
            <ThemedText style={styles.mediaCount}>
              {memory.sharedTo.length} {memory.sharedTo.length === 1 ? "item" : "items"}
            </ThemedText>
          </View>
        </View>
      ) : null}

      {/* Compact Sharing Indicator Pill */}
      {effectiveShares.length > 0 && (
        <View style={styles.sharePillContainer}>
          <TouchableOpacity
            style={[styles.compactSharePill, { backgroundColor: badgeBg, borderColor }]}
            onPress={memory.canShare ? handleSharePress : undefined}
            activeOpacity={memory.canShare ? 0.7 : 1}
            accessibilityLabel={`Shared with ${effectiveShares.length} recipients. Tap to manage sharing.`}
            testID={`memory-share-pill-${memory.id || "card"}`}
          >
            <Ionicons name="people-outline" size={12} color={tintColor} style={{ marginRight: 4 }} />
            <ThemedText style={[styles.compactShareText, { color: tintColor }]}>
              Shared ({effectiveShares.length})
            </ThemedText>
          </TouchableOpacity>
        </View>
      )}

      {/* Contact & Groups Section */}
      {(() => {
        const linkedContacts = resolvedContacts;
        const linkedGroups = resolvedGroups;
        const totalLinked = linkedContacts.length + linkedGroups.length;
        if (totalLinked === 0) return null;
        return (
          <View style={styles.contactAndGroupsContainer}>
            <View style={styles.contactAndGroupsHeaderRow}>
              <Ionicons name="people-outline" size={11} color={tintColor} />
              <ThemedText type="stamp" style={[styles.contactAndGroupsStamp, { color: textMuted }]}>
                Contact &amp; Groups ({totalLinked})
              </ThemedText>
            </View>
            <View style={styles.contactAndGroupsChipsRow}>
              {linkedContacts.map((c: any, idx: number) => (
                <ThemedChip
                  key={`mem-contact-${c.id || c.contactId || idx}`}
                  entityType="contact"
                  id={c.id || c.contactId}
                  label={c.displayName || c.name || "Contact"}
                  thumbnailUrl={c.photoIconUrl || c.photoUrl}
                  compact={isSmall}
                />
              ))}
              {linkedGroups.map((g: any, idx: number) => (
                <ThemedChip
                  key={`mem-group-${g.id || g.groupId || idx}`}
                  entityType="group"
                  id={g.id || g.groupId}
                  label={g.name || g.displayName || "Group"}
                  compact={isSmall}
                />
              ))}
            </View>
          </View>
        );
      })()}

      {/* Tags Section */}
      <TagListStrip
        entityId={memory.id || ""}
        entityType="memory"
        tags={memory.tags}
        canEdit={effectiveCanEditTags}
        isOwner={effectiveIsOwnerForTags}
        currentUserId={currentUserId}
        compact={isSmall}
      />

      {(() => {
        const totalMediaCount = effectiveMedia.length;
        const createdFormatted = formatCardTimestamp(memory.createdAt);
        return (
          <View style={[styles.cardFooter, { borderTopColor: borderColor, flexDirection: "row", justifyContent: "space-between", alignItems: "center" }]}>
            <ThemedText style={styles.mediaCount}>
              📖 {totalMediaCount} {totalMediaCount === 1 ? "Media item" : "Media items"}
            </ThemedText>
            {createdFormatted ? (
              <ThemedText style={[styles.mediaCount, { fontSize: 11, opacity: 0.8 }]}>
                🕒 Created: {createdFormatted}
              </ThemedText>
            ) : null}
          </View>
        );
      })()}

      <ShareSelector
        visible={isExpanded}
        onClose={handleSharePress}
        itemTitle={memory.title}
        itemType="memory"
        shares={effectiveShares}
        contacts={availableContacts || []}
        groups={availableGroups || []}
        isLoading={isLoadingShares}
        canShare={memory.canShare}
        canUnshare={memory.canShare}
        onShare={onShareTarget || (() => {})}
        onUnshare={onUnshare || (() => {})}
        testID={`memory-share-picker-${memory.id || "card"}`}
      />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    padding: 12,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#EDE8DF",
    marginBottom: 6,
    gap: 8,
  },
  cardCompact: {
    padding: 6,
    borderRadius: 4,
  },
  cardTopRightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    zIndex: 15,
  },
  cardHeaderAvatarContainer: {
    width: 38,
    height: 38,
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 1,
    flexShrink: 0,
  },
  cardHeaderAvatar: {
    width: 38,
    height: 38,
    borderRadius: 4,
  },
  avatarPlaceholder: {
    width: 38,
    height: 38,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  symbolsGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 4,
  },
  cardHeaderLeft: {
    flex: 1,
    paddingRight: 6,
  },
  cardTopRightAction: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 15,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
    marginBottom: 2,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
    flex: 1,
  },
  cardTitleCompact: {
    fontSize: 11,
    fontWeight: "600",
  },
  roleBadge: {
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  viewerBadge: {
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  editorBadge: {
    backgroundColor: "rgba(52, 211, 153, 0.1)", // Green tint matching dynamic badge
  },
  roleBadgeText: {
    fontSize: 8.5,
    fontWeight: "bold",
    textTransform: "uppercase",
    opacity: 0.8,
  },
  roleBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 2,
  },
  gridActionRow: {
    flexDirection: "row",
    gap: 2,
  },
  gridActionBtn: {
    padding: 3,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  gridDeleteBtn: {
    backgroundColor: "rgba(217, 48, 37, 0.1)",
  },
  gridActionIcon: {
    fontSize: 10,
    opacity: 0.6,
  },
  cardDate: {
    fontSize: 9.5,
    opacity: 0.5,
    marginBottom: 1,
  },
  dateFieldWrapper: {
    flexDirection: "column",
    maxWidth: 165,
    minWidth: 110,
  },
  dateFieldRow: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 3,
    paddingHorizontal: 4,
    height: 22,
    gap: 3,
  },
  dateInput: {
    fontSize: 9.5,
    paddingVertical: 0,
    paddingHorizontal: 2,
    flex: 1,
    minWidth: 65,
  },
  datePickerIconBtn: {
    justifyContent: "center",
    alignItems: "center",
  },
  datePickerTriggerBtn: {
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  dateErrorText: {
    fontSize: 8,
    marginTop: 1,
  },
  ownerText: {
    fontSize: 9.5,
    opacity: 0.6,
    fontStyle: "italic",
  },
  cardDesc: {
    fontSize: 10.5,
    marginBottom: 4,
    opacity: 0.8,
    lineHeight: 14,
  },
  noDesc: {
    fontStyle: "italic",
    opacity: 0.4,
  },
  cardLocation: {
    fontSize: 9.5,
    marginBottom: 4,
    opacity: 0.7,
  },
  cardRatingRow: {
    marginVertical: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  cardFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
    paddingTop: 4,
    marginTop: 4,
  },
  mediaCount: {
    fontSize: 9.5,
    opacity: 0.5,
  },
  actionRow: {
    flexDirection: "row",
    gap: 4,
  },
  shareBtn: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    borderRadius: 0,
  },
  shareText: {
    fontSize: 10,
    fontWeight: "bold",
  },
  actionBtn: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  actionText: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.7,
  },
  deleteBtn: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    backgroundColor: "rgba(217, 48, 37, 0.1)",
    borderRadius: 0,
  },
  deleteText: {
    fontSize: 10,
    color: "#d93025",
    fontWeight: "bold",
  },
  sharePillContainer: {
    marginTop: 3,
    marginBottom: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  compactSharePill: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: ButtonPaddings.compact.paddingVertical,
    paddingHorizontal: ButtonPaddings.compact.paddingHorizontal,
    borderWidth: 1,
    borderRadius: 0,
    alignSelf: "flex-start",
  },
  compactShareText: {
    fontSize: 11,
    fontWeight: "600",
  },
  sharesContainer: {
    marginTop: 2,
    marginBottom: 4,
  },
  sharesHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 3,
  },
  manageSharesText: {
    fontSize: 9.5,
    fontWeight: "600",
  },
  inlineSharingSection: {
    marginTop: 4,
    marginBottom: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  inlineSharingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  inlineSharingTitle: {
    fontSize: 11,
    letterSpacing: -0.2,
  },
  collapseBtn: {
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 0,
  },
  collapseBtnText: {
    fontSize: 9.5,
    fontWeight: "600",
    opacity: 0.7,
  },
  sharesLabel: {
    fontSize: 9,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
  },
  sharesList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3,
  },
  mediaPart: {
    marginTop: 3,
    marginBottom: 3,
    gap: 3,
  },
  mediaThumbScroll: {
    flexDirection: "row",
    marginBottom: 2,
  },
  mediaThumb: {
    width: 40,
    height: 40,
    borderRadius: 0,
    marginRight: 4,
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  mediaThumbLarge: {
    width: 140,
    height: 140,
    borderRadius: 0,
    marginRight: 6,
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  // Details view styles
  detailsRowContainer: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    marginVertical: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  detailsRowMain: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 3,
    paddingHorizontal: 6,
    gap: 6,
  },
  detailsThumb: {
    width: 28,
    height: 28,
    borderRadius: 0,
    overflow: "hidden",
  },
  detailsImage: {
    width: 28,
    height: 28,
  },
  detailsPlaceholder: {
    justifyContent: "center",
    alignItems: "center",
  },
  detailsInfo: {
    flex: 1,
    justifyContent: "center",
    gap: 1,
  },
  detailsNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  detailsFileName: {
    fontSize: 11,
    fontWeight: "600",
    flexShrink: 1,
  },
  detailsSubRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexWrap: "wrap",
  },
  detailsMetaText: {
    fontSize: 9.5,
    opacity: 0.65,
  },
  inlineRoleBadge: {
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 0,
  },
  gridRoleBadgeText: {
    fontSize: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    opacity: 0.8,
  },
  // Compact view styles (14 columns)
  compactModeCard: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    paddingVertical: 4,
    paddingHorizontal: 5,
    borderRadius: 0,
    borderWidth: 1,
    minHeight: 38,
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 1,
  },
  compactModeTitle: {
    fontSize: 9.5,
    fontWeight: "600",
    lineHeight: 12,
  },
  detailsCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 0,
    borderWidth: 1.5,
    borderColor: "rgba(150, 150, 150, 0.4)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  smallSelectionBadge: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 14,
    height: 14,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 14,
  },
  compactTopRightAction: {
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 15,
  },
  detailsTopRightAction: {
    alignSelf: "flex-start",
    marginTop: 2,
  },
  selectionBadge: {
    position: "absolute",
    top: 6,
    right: 34,
    width: 20,
    height: 20,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 14,
  },
  selectionText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
  },
  contactAndGroupsContainer: {
    marginTop: 4,
    marginBottom: 4,
    gap: 3,
  },
  contactAndGroupsHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  contactAndGroupsStamp: {
    fontSize: 8.5,
    letterSpacing: 0.6,
  },
  contactAndGroupsChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
});

